import { useEffect, useState } from 'react';
import { FiCopy, FiGift } from 'react-icons/fi';

import { ConfettiCanvas } from '@/components/ConfettiCanvas/ConfettiCanvas';
import { useRoles } from '@/hooks/useRoles';
import { useReferralQuery } from '@/store/subscriptions/subscriptions.store';
import { OWNER_GROUP_ROLES } from '@/utils/roles';

import { fmtDate } from './subscription-display';
import s from './SubscriptionSettingsPage.module.scss';

/**
 * One free month per person who signs up through the link. A product rule the API knows
 * nothing about — it reports the head count only.
 */
const FREE_MONTHS_PER_REFERRAL = 1;

/** How long "Copied" stays on the button after a copy. */
const COPIED_MS = 2200;

const earnedLabel = (referrals: number): string => {
  const months = referrals * FREE_MONTHS_PER_REFERRAL;
  if (!months) return 'No free months yet';
  return `${months} month${months === 1 ? '' : 's'} earned`;
};

type ReferralCardProps = {
  /** When the owner's soonest trial ends; `null` when no vessel is on trial. */
  trialEndsAt: number | null;
};

/**
 * The invite link, and — for owners only — what it has earned them.
 *
 * Free months accrue against a vessel subscription, so only owners are told about the
 * reward; everyone else gets the plain "share the app" version of the same link.
 *
 * Read-only by necessity: the people service exposes referral as three fields on the
 * person record and offers no endpoint to send an invite, redeem a code or regenerate
 * the link, so this shares the link and reports the count — nothing more.
 */
export const ReferralCard = ({ trialEndsAt }: ReferralCardProps) => {
  const { data: referral, isPending } = useReferralQuery();
  const { hasAnyRole } = useRoles();
  const isOwner = hasAnyRole(OWNER_GROUP_ROLES);
  const [copied, setCopied] = useState(false);
  // Bumped per copy so a repeat copy re-fires the burst and restarts the timer below.
  const [burst, setBurst] = useState(0);

  useEffect(() => {
    if (!burst) return;
    const timer = window.setTimeout(() => setCopied(false), COPIED_MS);
    return () => window.clearTimeout(timer);
  }, [burst]);

  const link = referral?.link ?? null;

  const copyLink = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
    } catch {
      // Clipboard denied or unavailable (insecure origin) — nothing was copied, so
      // there is nothing to celebrate.
      return;
    }
    setCopied(true);
    setBurst((count) => count + 1);
  };

  const linkPlaceholder = isPending ? 'Loading your invite link…' : 'No invite link yet';

  return (
    <div className={s.referral}>
      <div className={s.referralTop}>
        <span className={s.giftIcon}>
          <FiGift />
        </span>
        <span className={s.referralHeadText}>
          <h3 className={s.referralTitle}>
            {isOwner ? 'Extend your trial free' : 'Share the app'}
          </h3>
          <p className={s.referralBlurb}>
            {isOwner
              ? `Get ${FREE_MONTHS_PER_REFERRAL} free month for every person who joins with your link.`
              : 'Share your invite link with anyone who would find the app useful.'}
          </p>
        </span>
      </div>

      {copied && (
        <div className={s.celebrate} aria-hidden="true">
          <div className={s.celebrateMsg}>
            <span className={s.celebrateIcon}>
              <FiGift />
            </span>
            <span className={s.celebrateText}>Thank you for sharing VesselHaven.</span>
          </div>
        </div>
      )}

      {isOwner && (
        <div className={s.referralMeta}>
          {earnedLabel(referral?.count ?? 0)}
          {trialEndsAt !== null && (
            <>
              {' · trial ends '}
              <span className={s.metaAccent}>{fmtDate(trialEndsAt)}</span>
            </>
          )}
        </div>
      )}

      <div className={s.referralCopy}>
        <div className={s.codeField} title={link ?? undefined}>
          {link ?? linkPlaceholder}
        </div>
        <button
          type="button"
          className={`${s.copyBtn} ${copied ? s.copyBtnDone : ''}`}
          onClick={copyLink}
          disabled={!link}
        >
          <FiCopy />
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>

      <ConfettiCanvas fireKey={burst > 0 ? String(burst) : null} />
    </div>
  );
};
